/* eslint max-lines: ["error", { "max": 1458 }] */
import { MantineTheme } from '@mantine/core';
import { IconHelp, IconPencil } from '@tabler/icons-react';
import {
  GetEmployerDto,
  PayrollCycle,
  PayrollReportView,
} from '@zorro/clients';
import {
  APP_ROUTES,
  getSubEntityLabel,
  getWeeksFromPayrollCycle,
} from '@zorro/common-utils';
import { Button } from '@zorro/design-system/v2/Button';
import { Checkbox } from '@zorro/design-system/v2/Checkbox';
import { Link } from '@zorro/design-system/v2/Link';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import {
  formatCurrencyEnUs,
  formatDateEnLocale,
} from '@zorro/shared/formatters';
import { recordsPerPage } from '@zorro/types';
import {
  Box,
  Center,
  DATATABLE_ROW_HEIGHT,
  EmptyIcon,
  Flex,
  Group,
  Icon,
  Stack,
  Text,
  brand,
} from '@zorro/web/design-system';
import {
  exportToCsv,
  getPayrollCycleLabel,
  useImpersonation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { DataTableSortStatus } from 'mantine-datatable';
import { useEffect, useMemo, useState } from 'react';

import { useAnalytics } from '../../Analytics';
import { DateFilter, Filter, SelectFilter } from '../../Filters';
import { SearchableDataTable } from '../../SearchableDataTable';
import { TableTextCell } from '../../TableTextCell';
import {
  DisplayActionType,
  PayrollRow,
  PayrollViewToDataMap,
} from '../PayrollReportTypes';
import {
  getAvailableViewsForPayroll,
  getColumnLabel,
  getCountForView,
  getFilteredSortedSearchedRecordsForPayroll,
  payrollActionTypeLabelConfig,
  payrollReportColumns,
  prepareRecordsForExportToCsv,
} from '../PayrollReportUtils';
import { PayrollActionTypeBadge } from './ActionTypeBadge';
import { PayrollCell } from './PayrollCell';

type Props = {
  employer: GetEmployerDto;
  payrollViewToDataMap: PayrollViewToDataMap;
  currentView: PayrollReportView;
  setCurrentView: (view: PayrollReportView) => void;
  payrollCycle: PayrollCycle;
  payPeriodStartDate: Date;
  payPeriodEndDate: Date | null;
  indicateChangesSince: Date | null;
  onEditReportClick: () => void;
  isDon: boolean;
};

export function PayrollDataTable({
  employer,
  payrollViewToDataMap,
  currentView,
  setCurrentView,
  payrollCycle,
  payPeriodStartDate,
  payPeriodEndDate,
  indicateChangesSince,
  onEditReportClick,
  isDon,
}: Props) {
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState<string>('');
  const [filters, setFilters] = useState<Filter<PayrollRow>[]>([]);
  const [viewFullTable, setViewFullTable] = useState<boolean>(false);
  const { sendEvent } = useAnalytics();

  const formattedStartDate = formatDateEnLocale(payPeriodStartDate);
  const formattedEndDate = payPeriodEndDate
    ? formatDateEnLocale(payPeriodEndDate)
    : '';
  const formattedIndicateChangesSince = indicateChangesSince
    ? formatDateEnLocale(indicateChangesSince)
    : '';

  const { employee: impersonatedEmployee, isImpersonated } = useImpersonation();
  const impersonatedEmployeeId = isImpersonated
    ? impersonatedEmployee?.id
    : undefined;

  const { data: subEntities2 = [] } = useMonolithQuery({
    method: 'subEntity2ControllerFindMany',
    params: [employer.id],
  });

  const isSubEntity2Enabled = subEntities2.length > 0;

  const handleCurrentView = async (view: string) => {
    setCurrentView(view as PayrollReportView);
    setPage(1);
    setSortStatus({
      columnAccessor:
        view === PayrollReportView.ACTIONS_REQUIRED_SINCE
          ? 'lastUpdate'
          : 'firstName',
      direction:
        view === PayrollReportView.ACTIONS_REQUIRED_SINCE ? 'desc' : 'asc',
    });
    if (view === PayrollReportView.ACTIONS_REQUIRED_SINCE) {
      await sendEvent('click_actions_required_since_view', {
        row_count: getCountForView(availableViews, view),
      });
    }
    if (view === PayrollReportView.FULL_PAY_PERIOD) {
      await sendEvent('click_full_pay_period_report_view', {
        row_count: getCountForView(availableViews, view),
      });
    }
  };

  const [sortStatus, setSortStatus] = useState<DataTableSortStatus>({
    columnAccessor:
      currentView === PayrollReportView.ACTIONS_REQUIRED_SINCE
        ? 'lastUpdate'
        : 'firstName',
    direction:
      currentView === PayrollReportView.ACTIONS_REQUIRED_SINCE ? 'desc' : 'asc',
  });

  const onSortStatusChange = async (value: DataTableSortStatus) => {
    setSortStatus(value);
    await sendEvent('sort_report_column', {
      column_name: getColumnLabel(value.columnAccessor),
      sort_direction: value.direction,
    });
  };

  const filteredEmployeesViewToDataMap = useMemo(() => {
    const map: PayrollViewToDataMap = new Map();

    payrollViewToDataMap.forEach(({ data, isLoading }, view) => {
      const filteredData = getFilteredSortedSearchedRecordsForPayroll(
        data,
        filters,
        sortStatus,
        searchValue
      );
      map.set(view, { isLoading, data: filteredData });
    });

    return map;
  }, [payrollViewToDataMap, filters, sortStatus, searchValue]);

  const availableViews = getAvailableViewsForPayroll(
    filteredEmployeesViewToDataMap,
    formattedIndicateChangesSince
  );

  useEffect(() => {
    const isCurrentViewAvailable = availableViews.some(
      ({ value }) => currentView === value
    );
    if (!isCurrentViewAvailable) {
      setCurrentView(PayrollReportView.ACTIONS_REQUIRED_SINCE);
    }
  }, [availableViews, currentView, setCurrentView]);

  const { data: records = [], isLoading: isLoading } =
    filteredEmployeesViewToDataMap.get(currentView) || {};

  const pageRecords = records.slice(
    (page - 1) * recordsPerPage,
    page * recordsPerPage
  );

  const downloadEmployees = async () => {
    const fileName = `Payroll Report - ${employer.name} - ${formattedStartDate} - ${formattedEndDate}.csv`;
    const columns =
      currentView === PayrollReportView.ACTIONS_REQUIRED_SINCE
        ? payrollReportColumns
        : payrollReportColumns.filter(
            (column) => column.value !== 'actionType'
          );
    const recordsForCsv = prepareRecordsForExportToCsv(records);
    exportToCsv(recordsForCsv, fileName, columns);
    await sendEvent('download_table', {
      row_count: recordsForCsv.length,
    });
  };

  const handleSearchValueChange = (value: string) => {
    setSearchValue(value);
    setPage(1);
  };

  const handleFilterChange = async (value: Filter<PayrollRow>[]) => {
    setFilters(value);
    setPage(1);
    await sendEvent('filter_report_column', {
      filters: value.map((filter) => ({
        column_name: getColumnLabel(filter.type),
        filter_criteria: filter.value,
      })),
    });
  };

  const createSelectOptions = <T extends keyof PayrollRow>(
    property: T,
    renderOption?: (property: PayrollRow[T]) => string
  ) => {
    const result = [];

    const unfilteredPayrollReportRows =
      payrollViewToDataMap.get(currentView)?.data || [];

    const set = new Set(
      unfilteredPayrollReportRows.flatMap((row) => row[property] as string)
    );

    const filteredOptions = [...set].filter(Boolean).map((cls) => ({
      value: cls,
      label: renderOption?.(cls as unknown as PayrollRow[T]) ?? cls,
    }));
    result.push(...filteredOptions);
    return result;
  };

  const shouldShowIdColumn = records.some((row) => row?.idFromEmployer !== '-');

  const shouldShowSubEntityColumn = records.some(
    (row) => row.subEntity !== '-'
  );

  const shouldShowSubEntity2Column =
    isSubEntity2Enabled && records.some((row) => row.subEntity2 !== '-');
  const weeks = getWeeksFromPayrollCycle(payrollCycle);

  return (
    <SearchableDataTable
      rowHeight={DATATABLE_ROW_HEIGHT}
      heading="Payroll report"
      subHeading={
        <Group gap="lg">
          <Group gap="xxs">
            <Text c="zorroGray.4">Payroll cycle:</Text>
            <Text c="zorroCoal.9">{getPayrollCycleLabel(payrollCycle)}</Text>
          </Group>
          <Group gap="xxs">
            <Text c="zorroGray.4">Pay period:</Text>
            <Text c="zorroCoal.9">{`${formattedStartDate}-${formattedEndDate}`}</Text>
          </Group>
          <Group gap="xxs">
            <Text c="zorroGray.4">Indicate changes since:</Text>
            <Text c="zorroCoal.9">{formattedIndicateChangesSince}</Text>
          </Group>
        </Group>
      }
      componentsAlignedRightToHeading={
        <Button
          variant="secondary"
          size="md"
          onClick={onEditReportClick}
          disabled={isLoading}
          leftSection={<IconPencil color={brand.zorroGray400} />}
        >
          Edit report
        </Button>
      }
      componentsLeftToDownloadButton={
        <Checkbox
          label="View full table"
          checked={viewFullTable}
          onChange={async () => {
            setViewFullTable(!viewFullTable);
            await sendEvent('activate_view_full_table', {
              detailed_view_enabled: !viewFullTable,
            });
          }}
        />
      }
      onDownload={downloadEmployees}
      searchValue={searchValue}
      onSearchChange={handleSearchValueChange}
      onSearchIconClick={async () => await sendEvent('search_report_table')}
      isSearchEnabled
      filtersCount={filters.length}
      clearFilters={() => setFilters([])}
      availableViews={availableViews}
      currentView={currentView}
      setCurrentView={handleCurrentView}
      shouldPinFirstColumn
      shouldGrow
      shouldFillWidth
      records={isLoading ? undefined : pageRecords}
      highlightOnHover
      groups={[
        {
          id: 'employeeDetails',
          title: (
            <Box ta="center" w="100%">
              Employee details
            </Box>
          ),
          columns: [
            {
              accessor: 'firstName',
              sortable: true,
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                firstName,
                employeeId,
                onboardingPeriodId,
              }: PayrollRow) => {
                const href = isDon
                  ? APP_ROUTES.DON_EMPLOYEE_PLANS_TAB({
                      employerId: employer.id,
                      employeeId,
                      onboardingPeriodId,
                      impersonatedEmployeeId,
                    })
                  : APP_ROUTES.OPS_EMPLOYEE_PLANS_TAB({
                      employerId: employer.id,
                      employeeId,
                      onboardingPeriodId,
                      impersonatedEmployeeId,
                    });
                return (
                  <PayrollCell
                    shouldShowPayrollForOneTimeCorrection={
                      shouldShowPayrollForOneTimeCorrection
                    }
                    cellValue={
                      <Link
                        href={href}
                        target="_blank"
                        prefetch={false}
                        rightSection={null}
                      >
                        {firstName}
                      </Link>
                    }
                  />
                );
              },
            },
            {
              accessor: 'lastName',
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                lastName,
              }: PayrollRow) => (
                <PayrollCell
                  shouldShowPayrollForOneTimeCorrection={
                    shouldShowPayrollForOneTimeCorrection
                  }
                  cellValue={lastName}
                />
              ),
              sortable: true,
            },
            {
              accessor: 'idFromEmployer',
              title: 'ID',
              noWrap: true,
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                idFromEmployer,
              }: PayrollRow) => (
                <PayrollCell
                  shouldShowPayrollForOneTimeCorrection={
                    shouldShowPayrollForOneTimeCorrection
                  }
                  cellValue={idFromEmployer}
                />
              ),
              hidden: !shouldShowIdColumn,
              sortable: true,
            },
            {
              accessor: 'subEntity',
              title: getSubEntityLabel(),
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                subEntity,
              }: PayrollRow) => (
                <PayrollCell
                  shouldShowPayrollForOneTimeCorrection={
                    shouldShowPayrollForOneTimeCorrection
                  }
                  cellValue={subEntity}
                />
              ),
              hidden: !shouldShowSubEntityColumn,
              sortable: true,
              filtering: filters.some((filter) => filter.type === 'subEntity'),
              filter: (
                <Group p="xs">
                  <SelectFilter
                    title={
                      shouldShowSubEntity2Column
                        ? 'Sub-entity 1:'
                        : 'Sub-entity:'
                    }
                    selectOptions={createSelectOptions(
                      'subEntity',
                      (subEntity) => subEntity || '-'
                    )}
                    filterBy="subEntity"
                    setFilters={handleFilterChange}
                    filters={filters}
                  />
                </Group>
              ),
            },
            {
              accessor: 'subEntity2',
              title: 'Sub-entity 2',
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                subEntity2,
              }: PayrollRow) => (
                <PayrollCell
                  shouldShowPayrollForOneTimeCorrection={
                    shouldShowPayrollForOneTimeCorrection
                  }
                  cellValue={subEntity2}
                />
              ),
              hidden: !shouldShowSubEntity2Column,
              sortable: true,
              filtering: filters.some((filter) => filter.type === 'subEntity2'),
              filter: (
                <Group p="xs">
                  <SelectFilter
                    title="Sub-entity 2:"
                    selectOptions={createSelectOptions(
                      'subEntity2',
                      (subEntity2) => subEntity2 || '-'
                    )}
                    filterBy="subEntity2"
                    setFilters={handleFilterChange}
                    filters={filters}
                  />
                </Group>
              ),
            },
            {
              accessor: 'email',
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                email,
              }: PayrollRow) => (
                <PayrollCell
                  shouldShowPayrollForOneTimeCorrection={
                    shouldShowPayrollForOneTimeCorrection
                  }
                  cellValue={email}
                />
              ),
              hidden: !viewFullTable,
            },
            {
              accessor: 'dateOfBirth',
              title: 'Date of birth',
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                dateOfBirth,
              }: PayrollRow) => (
                <PayrollCell
                  shouldShowPayrollForOneTimeCorrection={
                    shouldShowPayrollForOneTimeCorrection
                  }
                  cellValue={dateOfBirth}
                />
              ),
              hidden: !viewFullTable,
              filtering: filters.some(
                (filter) => filter.type === 'dateOfBirth'
              ),

              filter: ({ close }) => (
                <DateFilter
                  setFilters={handleFilterChange}
                  filterBy="dateOfBirth"
                  filters={filters}
                  onComplete={close}
                />
              ),
            },
            {
              accessor: 'state',
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                state,
              }: PayrollRow) => (
                <PayrollCell
                  shouldShowPayrollForOneTimeCorrection={
                    shouldShowPayrollForOneTimeCorrection
                  }
                  cellValue={state}
                />
              ),
              hidden: !viewFullTable,
              sortable: true,
              filtering: filters.some((filter) => filter.type === 'state'),
              filter: (
                <Group p="xs">
                  <SelectFilter
                    title="State:"
                    selectOptions={createSelectOptions('state')}
                    filterBy="state"
                    setFilters={handleFilterChange}
                    filters={filters}
                  />
                </Group>
              ),
            },
            {
              accessor: 'employeeClass',
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                employeeClass,
              }: PayrollRow) => (
                <PayrollCell
                  shouldShowPayrollForOneTimeCorrection={
                    shouldShowPayrollForOneTimeCorrection
                  }
                  cellValue={employeeClass}
                />
              ),
              hidden: !viewFullTable,
              filtering: filters.some(
                (filter) => filter.type === 'employeeClass'
              ),
              filter: (
                <Group p="xs">
                  <SelectFilter
                    title="Class:"
                    selectOptions={createSelectOptions(
                      'employeeClass',
                      (employeeClass) => employeeClass || '-'
                    )}
                    filterBy="employeeClass"
                    setFilters={handleFilterChange}
                    filters={filters}
                  />
                </Group>
              ),
            },
            {
              accessor: 'terminationDate',
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                terminationDate,
              }: PayrollRow) => (
                <PayrollCell
                  shouldShowPayrollForOneTimeCorrection={
                    shouldShowPayrollForOneTimeCorrection
                  }
                  cellValue={terminationDate}
                />
              ),
              hidden: !viewFullTable,
            },
          ],
          style: (theme: MantineTheme) => ({
            backgroundColor: theme.colors.zorroGray50,
          }),
        },
        {
          id: 'payrollSummary',
          title: (
            <Box ta="center" w="100%">
              Payroll summary
            </Box>
          ),
          columns: [
            {
              accessor: 'actionType',
              title: (
                <Group wrap="nowrap" gap="4">
                  <Text size="sm" fw={500} c={brand.zorroGray400}>
                    Action type
                  </Text>
                  <Tooltip
                    label="Indicates the type of action you need to take based on the enrollment change(s)"
                    position="top"
                    onMouseEnter={async () =>
                      await sendEvent('hover_tooltip', {
                        element: 'Action Type',
                        tooltip_text:
                          'Indicates the type of action you need to take based on the enrollment change(s)',
                      })
                    }
                  >
                    <Box pos="relative">
                      <Center>
                        <Icon
                          icon={IconHelp}
                          color="var(--color-icon-fill-system-secondary)"
                          size="0.75rem"
                        />
                      </Center>
                    </Box>
                  </Tooltip>
                </Group>
              ),
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                actionType,
              }: PayrollRow) =>
                actionType ? (
                  <Stack gap="xs">
                    <PayrollActionTypeBadge actionType={actionType} />
                    {shouldShowPayrollForOneTimeCorrection && (
                      <PayrollActionTypeBadge
                        actionType={DisplayActionType.ONE_TIME_CORRECTION}
                      />
                    )}
                  </Stack>
                ) : (
                  '-'
                ),
              hidden: currentView !== PayrollReportView.ACTIONS_REQUIRED_SINCE,
              width: 200,
              sortable: true,
              filtering: filters.some((filter) => filter.type === 'actionType'),
              filter: (
                <Group p="xs">
                  <SelectFilter
                    title="Action Type:"
                    selectOptions={createSelectOptions(
                      'actionType',
                      (actionType) => {
                        return actionType
                          ? payrollActionTypeLabelConfig[actionType]
                          : '-';
                      }
                    )}
                    filterBy="actionType"
                    setFilters={handleFilterChange}
                    filters={filters}
                  />
                </Group>
              ),
            },
            {
              accessor: 'deduction',
              title: (
                <Group wrap="nowrap" gap="4">
                  <Text size="sm" fw={500} c={brand.zorroGray400}>
                    Deduction
                  </Text>
                  <Tooltip
                    label={`Deduction amount adjusted to ${weeks} equal pay cycles per year`}
                    position="top"
                    onMouseEnter={async () =>
                      await sendEvent('hover_tooltip', {
                        element: 'Deduction',
                        tooltip_text: `Deduction amount adjusted to ${weeks} equal pay cycles per year`,
                      })
                    }
                  >
                    <Box pos="relative">
                      <Center>
                        <Icon
                          icon={IconHelp}
                          color="var(--color-icon-fill-system-secondary)"
                          size="0.75rem"
                        />
                      </Center>
                    </Box>
                  </Tooltip>
                </Group>
              ),
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                shouldShowEnrollmentForOneTimeCorrection,
                deduction,
                oneTimeDeduction,
              }: PayrollRow) => (
                <PayrollCell
                  isPayrollColumn
                  shouldShowPayrollForOneTimeCorrection={
                    shouldShowPayrollForOneTimeCorrection
                  }
                  shouldShowEnrollmentForOneTimeCorrection={
                    shouldShowEnrollmentForOneTimeCorrection
                  }
                  cellValue={formatCurrencyEnUs(deduction, {
                    minimumFractionDigits: 2,
                  })}
                  secondaryCellValue={formatCurrencyEnUs(oneTimeDeduction, {
                    minimumFractionDigits: 2,
                  })}
                />
              ),
            },
            {
              accessor: 'reimbursement',
              title: (
                <Group wrap="nowrap" gap="4">
                  <Text size="sm" fw={500} c={brand.zorroGray400}>
                    Reimbursement
                  </Text>
                  <Tooltip
                    label={`Reimbursement amount adjusted to ${weeks} equal pay cycles per year`}
                    position="top"
                    onMouseEnter={async () =>
                      await sendEvent('hover_tooltip', {
                        element: 'Reimbursement',
                        tooltip_text: `Reimbursement amount adjusted to ${weeks} equal pay cycles per year`,
                      })
                    }
                  >
                    <Box pos="relative">
                      <Center>
                        <Icon
                          icon={IconHelp}
                          color="var(--color-icon-fill-system-secondary)"
                          size="0.75rem"
                        />
                      </Center>
                    </Box>
                  </Tooltip>
                </Group>
              ),
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                shouldShowEnrollmentForOneTimeCorrection,
                reimbursement,
                oneTimeReimbursement,
              }: PayrollRow) => (
                <PayrollCell
                  isPayrollColumn
                  shouldShowPayrollForOneTimeCorrection={
                    shouldShowPayrollForOneTimeCorrection
                  }
                  shouldShowEnrollmentForOneTimeCorrection={
                    shouldShowEnrollmentForOneTimeCorrection
                  }
                  cellValue={formatCurrencyEnUs(reimbursement, {
                    minimumFractionDigits: 2,
                  })}
                  secondaryCellValue={formatCurrencyEnUs(oneTimeReimbursement, {
                    minimumFractionDigits: 2,
                  })}
                />
              ),
            },
            {
              accessor: 'employerContribution',
              title: (
                <Group wrap="nowrap" gap="4">
                  <Text size="sm" fw={500} c={brand.zorroGray400}>
                    Employer contribution
                  </Text>
                  <Tooltip
                    label={`Employer contribution amount adjusted to ${weeks} equal pay cycles per year`}
                    position="top"
                    onMouseEnter={async () =>
                      await sendEvent('hover_tooltip', {
                        element: 'Employer contribution',
                        tooltip_text: `Employer contribution amount adjusted to ${weeks} equal pay cycles per year`,
                      })
                    }
                  >
                    <Box pos="relative">
                      <Center>
                        <Icon
                          icon={IconHelp}
                          color="var(--color-icon-fill-system-secondary)"
                          size="0.75rem"
                        />
                      </Center>
                    </Box>
                  </Tooltip>
                </Group>
              ),
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                shouldShowEnrollmentForOneTimeCorrection,
                employerContribution,
                oneTimeEmployerContribution,
              }: PayrollRow) => (
                <PayrollCell
                  shouldShowPayrollForOneTimeCorrection={
                    shouldShowPayrollForOneTimeCorrection
                  }
                  isEnrollmentColumn
                  shouldShowEnrollmentForOneTimeCorrection={
                    shouldShowEnrollmentForOneTimeCorrection
                  }
                  cellValue={formatCurrencyEnUs(employerContribution, {
                    minimumFractionDigits: 2,
                  })}
                  secondaryCellValue={formatCurrencyEnUs(
                    oneTimeEmployerContribution,
                    { minimumFractionDigits: 2 }
                  )}
                />
              ),
            },
            {
              accessor: 'effectiveFrom',
              title: (
                <Group wrap="nowrap" gap="4">
                  <Text size="sm" fw={500} c={brand.zorroGray400}>
                    Effective from
                  </Text>
                  <Tooltip
                    label="The date the enrollment becomes effective"
                    position="top"
                    onMouseEnter={async () =>
                      await sendEvent('hover_tooltip', {
                        element: 'Effective From',
                        tooltip_text:
                          'The date the enrollment becomes effective',
                      })
                    }
                  >
                    <Box pos="relative">
                      <Center>
                        <Icon
                          icon={IconHelp}
                          color="var(--color-icon-fill-system-secondary)"
                          size="0.75rem"
                        />
                      </Center>
                    </Box>
                  </Tooltip>
                </Group>
              ),
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                shouldShowEnrollmentForOneTimeCorrection,
                effectiveFrom,
                oneTimeEffectiveFrom,
              }: PayrollRow) => (
                <PayrollCell
                  shouldShowPayrollForOneTimeCorrection={
                    shouldShowPayrollForOneTimeCorrection
                  }
                  isEnrollmentColumn
                  shouldShowEnrollmentForOneTimeCorrection={
                    shouldShowEnrollmentForOneTimeCorrection
                  }
                  cellValue={effectiveFrom}
                  secondaryCellValue={oneTimeEffectiveFrom}
                />
              ),
              sortable: true,
              filtering: filters.some(
                (filter) => filter.type === 'effectiveFrom'
              ),

              filter: ({ close }) => (
                <DateFilter
                  filterBy="effectiveFrom"
                  setFilters={handleFilterChange}
                  filters={filters}
                  onComplete={close}
                />
              ),
            },
            {
              accessor: 'effectiveUntil',
              title: (
                <Group wrap="nowrap" gap="4">
                  <Text size="sm" fw={500} c={brand.zorroGray400}>
                    Effective until
                  </Text>
                  <Tooltip
                    label="The last day of coverage, or the day before a new enrollment becomes effective"
                    position="top"
                    onMouseEnter={async () =>
                      await sendEvent('hover_tooltip', {
                        element: 'Effective Until',
                        tooltip_text:
                          'The last day of coverage, or the day before a new enrollment becomes effective',
                      })
                    }
                  >
                    <Box pos="relative">
                      <Center>
                        <Icon
                          icon={IconHelp}
                          color="var(--color-icon-fill-system-secondary)"
                          size="0.75rem"
                        />
                      </Center>
                    </Box>
                  </Tooltip>
                </Group>
              ),
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                shouldShowEnrollmentForOneTimeCorrection,
                effectiveUntil,
                oneTimeEffectiveUntil,
              }: PayrollRow) => (
                <PayrollCell
                  shouldShowPayrollForOneTimeCorrection={
                    shouldShowPayrollForOneTimeCorrection
                  }
                  isEnrollmentColumn
                  shouldShowEnrollmentForOneTimeCorrection={
                    shouldShowEnrollmentForOneTimeCorrection
                  }
                  cellValue={effectiveUntil}
                  secondaryCellValue={oneTimeEffectiveUntil}
                />
              ),
              sortable: true,
              filtering: filters.some(
                (filter) => filter.type === 'effectiveUntil'
              ),

              filter: ({ close }) => (
                <DateFilter
                  filterBy="effectiveUntil"
                  setFilters={handleFilterChange}
                  filters={filters}
                  onComplete={close}
                />
              ),
            },
            {
              accessor: 'lastUpdate',
              noWrap: true,
              title: (
                <Group wrap="nowrap" gap="4">
                  <Text size="sm" fw={500} c={brand.zorroGray400}>
                    Last update
                  </Text>
                  <Tooltip
                    label="Date this enrollment information was last updated in the system"
                    position="top"
                    onMouseEnter={async () =>
                      await sendEvent('hover_tooltip', {
                        element: 'Last Update',
                        tooltip_text:
                          'Date this enrollment information was last updated in the system',
                      })
                    }
                  >
                    <Box pos="relative">
                      <Center>
                        <Icon
                          icon={IconHelp}
                          color="var(--color-icon-fill-system-secondary)"
                          size="0.75rem"
                        />
                      </Center>
                    </Box>
                  </Tooltip>
                </Group>
              ),
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                shouldShowEnrollmentForOneTimeCorrection,
                lastUpdate,
                oneTimeLastUpdate,
              }: PayrollRow) => (
                <PayrollCell
                  shouldShowPayrollForOneTimeCorrection={
                    shouldShowPayrollForOneTimeCorrection
                  }
                  isEnrollmentColumn
                  shouldShowEnrollmentForOneTimeCorrection={
                    shouldShowEnrollmentForOneTimeCorrection
                  }
                  cellValue={lastUpdate}
                  secondaryCellValue={oneTimeLastUpdate}
                />
              ),
              sortable: true,
              filtering: filters.some((filter) => filter.type === 'lastUpdate'),

              filter: ({ close }) => (
                <DateFilter
                  filterBy="lastUpdate"
                  setFilters={handleFilterChange}
                  filters={filters}
                  onComplete={close}
                />
              ),
            },
          ],
        },
        {
          id: 'medicalPlan',
          title: (
            <Box ta="center" w="100%">
              Medical plan details
            </Box>
          ),
          columns: [
            {
              accessor: 'medicalPlanType',
              title: 'Family unit',
              hidden: !viewFullTable,
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                shouldShowEnrollmentForOneTimeCorrection,
                medicalPlanType,
                oneTimeMedicalPlanType,
              }: PayrollRow) => (
                <PayrollCell
                  shouldShowPayrollForOneTimeCorrection={
                    shouldShowPayrollForOneTimeCorrection
                  }
                  isEnrollmentColumn
                  shouldShowEnrollmentForOneTimeCorrection={
                    shouldShowEnrollmentForOneTimeCorrection
                  }
                  cellValue={medicalPlanType}
                  secondaryCellValue={oneTimeMedicalPlanType}
                />
              ),
              filtering: filters.some(
                (filter) => filter.type === 'medicalPlanType'
              ),
              filter: (
                <Group p="xs">
                  <SelectFilter
                    title="Family unit:"
                    selectOptions={createSelectOptions(
                      'medicalPlanType',
                      (medicalPlanType) => medicalPlanType || '-'
                    )}
                    filterBy="medicalPlanType"
                    setFilters={handleFilterChange}
                    filters={filters}
                  />
                </Group>
              ),
            },
            {
              accessor: 'medicalPlanCarrier',
              title: 'Carrier',
              hidden: !viewFullTable,
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                shouldShowEnrollmentForOneTimeCorrection,
                medicalPlanCarrier,
                oneTimeMedicalPlanCarrier,
              }: PayrollRow) => (
                <PayrollCell
                  shouldShowPayrollForOneTimeCorrection={
                    shouldShowPayrollForOneTimeCorrection
                  }
                  isEnrollmentColumn
                  shouldShowEnrollmentForOneTimeCorrection={
                    shouldShowEnrollmentForOneTimeCorrection
                  }
                  cellValue={
                    <TableTextCell
                      value={medicalPlanCarrier ?? null}
                      widthThreshold={120}
                    />
                  }
                  secondaryCellValue={
                    shouldShowEnrollmentForOneTimeCorrection ? (
                      <TableTextCell
                        value={oneTimeMedicalPlanCarrier ?? null}
                        widthThreshold={120}
                      />
                    ) : null
                  }
                />
              ),
              width: '160px',
              filtering: filters.some(
                (filter) => filter.type === 'medicalPlanCarrier'
              ),
              filter: (
                <Group p="xs">
                  <SelectFilter
                    title="Carrier:"
                    selectOptions={createSelectOptions(
                      'medicalPlanCarrier',
                      (medicalPlanCarrier) => medicalPlanCarrier || '-'
                    )}
                    filterBy="medicalPlanCarrier"
                    setFilters={handleFilterChange}
                    filters={filters}
                  />
                </Group>
              ),
            },
            {
              accessor: 'medicalPlanName',
              title: 'Plan name',
              hidden: !viewFullTable,
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                shouldShowEnrollmentForOneTimeCorrection,
                medicalPlanName,
                oneTimeMedicalPlanName,
              }: PayrollRow) => (
                <PayrollCell
                  shouldShowPayrollForOneTimeCorrection={
                    shouldShowPayrollForOneTimeCorrection
                  }
                  isEnrollmentColumn
                  shouldShowEnrollmentForOneTimeCorrection={
                    shouldShowEnrollmentForOneTimeCorrection
                  }
                  cellValue={
                    <TableTextCell
                      value={medicalPlanName ?? null}
                      widthThreshold={360}
                    />
                  }
                  secondaryCellValue={
                    shouldShowEnrollmentForOneTimeCorrection ? (
                      <TableTextCell
                        value={oneTimeMedicalPlanName ?? null}
                        widthThreshold={360}
                      />
                    ) : null
                  }
                />
              ),
              width: '400px',
            },
            {
              accessor: 'medicalPlanPremium',
              title: 'Monthly premium',
              hidden: !viewFullTable,
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                shouldShowEnrollmentForOneTimeCorrection,
                medicalPlanPremium,
                oneTimeMedicalPlanPremium,
              }: PayrollRow) => (
                <PayrollCell
                  shouldShowPayrollForOneTimeCorrection={
                    shouldShowPayrollForOneTimeCorrection
                  }
                  isEnrollmentColumn
                  shouldShowEnrollmentForOneTimeCorrection={
                    shouldShowEnrollmentForOneTimeCorrection
                  }
                  cellValue={formatCurrencyEnUs(medicalPlanPremium, {
                    minimumFractionDigits: 2,
                  })}
                  secondaryCellValue={formatCurrencyEnUs(
                    oneTimeMedicalPlanPremium,
                    { minimumFractionDigits: 2 }
                  )}
                />
              ),
            },
            {
              accessor: 'medicalPlanEmployerAllowance',
              title: 'Monthly employer contribution',
              hidden: !viewFullTable,
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                shouldShowEnrollmentForOneTimeCorrection,
                medicalPlanEmployerAllowance,
                oneTimeMedicalPlanEmployerAllowance,
              }: PayrollRow) => (
                <PayrollCell
                  shouldShowPayrollForOneTimeCorrection={
                    shouldShowPayrollForOneTimeCorrection
                  }
                  isEnrollmentColumn
                  shouldShowEnrollmentForOneTimeCorrection={
                    shouldShowEnrollmentForOneTimeCorrection
                  }
                  cellValue={formatCurrencyEnUs(medicalPlanEmployerAllowance, {
                    minimumFractionDigits: 2,
                  })}
                  secondaryCellValue={formatCurrencyEnUs(
                    oneTimeMedicalPlanEmployerAllowance,
                    { minimumFractionDigits: 2 }
                  )}
                />
              ),
            },
            {
              accessor: 'medicalPlanEmployeeContribution',
              title: 'Monthly employee contribution',
              hidden: !viewFullTable,
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                shouldShowEnrollmentForOneTimeCorrection,
                medicalPlanEmployeeContribution,
                oneTimeMedicalPlanEmployeeContribution,
              }: PayrollRow) => (
                <PayrollCell
                  shouldShowPayrollForOneTimeCorrection={
                    shouldShowPayrollForOneTimeCorrection
                  }
                  isEnrollmentColumn
                  shouldShowEnrollmentForOneTimeCorrection={
                    shouldShowEnrollmentForOneTimeCorrection
                  }
                  cellValue={formatCurrencyEnUs(
                    medicalPlanEmployeeContribution,
                    { minimumFractionDigits: 2 }
                  )}
                  secondaryCellValue={formatCurrencyEnUs(
                    oneTimeMedicalPlanEmployeeContribution,
                    { minimumFractionDigits: 2 }
                  )}
                />
              ),
            },
            {
              accessor: 'medicalPlanEmployeeSelfPayment',
              title: 'Monthly employee self payment',
              hidden: !viewFullTable,
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                shouldShowEnrollmentForOneTimeCorrection,
                medicalPlanEmployeeSelfPayment,
                oneTimeMedicalPlanEmployeeSelfPayment,
              }: PayrollRow) => (
                <PayrollCell
                  shouldShowPayrollForOneTimeCorrection={
                    shouldShowPayrollForOneTimeCorrection
                  }
                  isEnrollmentColumn
                  shouldShowEnrollmentForOneTimeCorrection={
                    shouldShowEnrollmentForOneTimeCorrection
                  }
                  cellValue={formatCurrencyEnUs(
                    medicalPlanEmployeeSelfPayment,
                    { minimumFractionDigits: 2 }
                  )}
                  secondaryCellValue={formatCurrencyEnUs(
                    oneTimeMedicalPlanEmployeeSelfPayment,
                    { minimumFractionDigits: 2 }
                  )}
                />
              ),
            },
            {
              accessor: 'planMarket',
              hidden: !viewFullTable,
              title: (
                <Group wrap="nowrap" gap="4">
                  <Text size="sm" fw={500} c={brand.zorroGray400}>
                    Plan market
                  </Text>
                  <Tooltip
                    label="Indicates deduction type. Use pre-tax for Off-exchange, and post-tax otherwise"
                    position="top"
                    onMouseEnter={async () =>
                      await sendEvent('hover_tooltip', {
                        element: 'Plan market',
                        tooltip_text:
                          'Indicates deduction type. Use pre-tax for Off-exchange, and post-tax otherwise',
                      })
                    }
                  >
                    <Box pos="relative">
                      <Center>
                        <Icon
                          icon={IconHelp}
                          color="var(--color-icon-fill-system-secondary)"
                          size="0.75rem"
                        />
                      </Center>
                    </Box>
                  </Tooltip>
                </Group>
              ),
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                shouldShowEnrollmentForOneTimeCorrection,
                planMarket,
                oneTimePlanMarket,
              }: PayrollRow) => (
                <PayrollCell
                  shouldShowPayrollForOneTimeCorrection={
                    shouldShowPayrollForOneTimeCorrection
                  }
                  isEnrollmentColumn
                  shouldShowEnrollmentForOneTimeCorrection={
                    shouldShowEnrollmentForOneTimeCorrection
                  }
                  cellValue={planMarket}
                  secondaryCellValue={oneTimePlanMarket}
                />
              ),
              width: '200px',
            },
            {
              accessor: 'isHsaEligible',
              title: 'HSA eligible',
              hidden: !viewFullTable,
              width: '120px',
              render: ({
                shouldShowPayrollForOneTimeCorrection,
                shouldShowEnrollmentForOneTimeCorrection,
                isHsaEligible,
                oneTimeIsHsaEligible,
              }: PayrollRow) => (
                <PayrollCell
                  shouldShowPayrollForOneTimeCorrection={
                    shouldShowPayrollForOneTimeCorrection
                  }
                  isEnrollmentColumn
                  shouldShowEnrollmentForOneTimeCorrection={
                    shouldShowEnrollmentForOneTimeCorrection
                  }
                  cellValue={isHsaEligible}
                  secondaryCellValue={oneTimeIsHsaEligible}
                />
              ),
            },
          ],
        },
      ]}
      minHeight={300}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      totalRecords={records.length}
      recordsPerPage={recordsPerPage}
      page={page}
      onPageChange={setPage}
      isLoading={isLoading}
      emptyState={
        currentView === PayrollReportView.ACTIONS_REQUIRED_SINCE && (
          <Stack gap="xs" align="center">
            <EmptyIcon />
            <Text>No actions found for this period</Text>
            <Flex gap="0.5rem">
              <Text c="var(--brand-grayscale-600)">
                To see employee status and amounts, view the
              </Text>
              <Button
                size="xs"
                px={0}
                variant="tertiary"
                onClick={() => {
                  setCurrentView(PayrollReportView.FULL_PAY_PERIOD);
                }}
              >
                <Text
                  c="zorroCoal.9"
                  size="sm"
                  fw="500"
                  style={{
                    textDecorationLine: 'underline',
                    textUnderlineOffset: '4px',
                  }}
                >
                  Full pay period report
                </Text>
              </Button>
            </Flex>
          </Stack>
        )
      }
    />
  );
}
